import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  getModbusServerStatus,
  getOpcuaServerRoleStatus,
  getPLCControllerOutputDelivery,
  getS7CommServerStatus,
  listEdgeGateways,
  listPLCControllers,
  listPLCFleetRollouts,
} from "../../../api";
import { GatewayMetric, GatewayStatusPill } from "../../../components/GatewayStatus";
import { useNotify } from "../../../ToastContext";
import chevronIcon from "../../../assets/chevron-right.svg";
import refreshIcon from "../../../assets/refresh.svg";
import {
  availableEvidence,
  buildPLCOverview,
  unavailableEvidence,
} from "./plc-overview-model";

const OUTPUT_READ_LIMIT = 8;
const OUTPUT_READ_CONCURRENCY = 4;
const OUTPUT_READ_DEADLINE_MS = 4000;

const evidenceFromResult = (result, fallback) => (
  result.status === "fulfilled"
    ? availableEvidence(result.value)
    : unavailableEvidence(fallback)
);

const readOneOutput = async (controller) => {
  const abort = new AbortController();
  let timeout;
  try {
    const deadline = new Promise((_, reject) => {
      timeout = window.setTimeout(() => {
        abort.abort();
        reject(new Error("Output evidence deadline exceeded."));
      }, OUTPUT_READ_DEADLINE_MS);
    });
    const status = await Promise.race([
      getPLCControllerOutputDelivery(controller.id, 1, abort.signal),
      deadline,
    ]);
    const targetName = String(controller?.target?.name || "").trim();
    return {
      controllerName: targetName ? `${controller.name} on ${targetName}` : controller.name,
      status,
    };
  } catch (_error) {
    const targetName = String(controller?.target?.name || "").trim();
    return {
      controllerName: targetName ? `${controller.name} on ${targetName}` : controller.name,
      status: null,
    };
  } finally {
    window.clearTimeout(timeout);
    abort.abort();
  }
};

export async function readBoundedControllerOutputs(controllers, cursor = 0) {
  const source = Array.isArray(controllers) ? controllers : [];
  const start = source.length > 0
    ? ((Math.trunc(Number(cursor) || 0) % source.length) + source.length) % source.length
    : 0;
  const selected = Array.from(
    { length: Math.min(OUTPUT_READ_LIMIT, source.length) },
    (_, index) => source[(start + index) % source.length]
  );
  const entries = new Array(selected.length);
  let workIndex = 0;
  const worker = async () => {
    while (workIndex < selected.length) {
      const index = workIndex;
      workIndex += 1;
      entries[index] = await readOneOutput(selected[index]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(OUTPUT_READ_CONCURRENCY, selected.length) }, worker)
  );
  return {
    attempted: selected.length,
    checked: entries.filter((entry) => entry?.status).length,
    total: source.length,
    entries,
    nextCursor: source.length > 0 ? (start + selected.length) % source.length : 0,
  };
}

const EvidenceState = ({ state }) => {
  const tone = ["Available", "Status loaded"].includes(state) ? "isPositive" : state === "Unavailable" ? "isWarning" : "isNeutral";
  return <GatewayStatusPill tone={tone}>{state}</GatewayStatusPill>;
};

const IssueRow = ({ issue }) => (
  <li className={`plcOverviewIssue is-${issue.severity}`}>
    <span className="plcOverviewIssueMark" aria-hidden="true" />
    <div>
      <strong>{issue.title}</strong>
      <span>{issue.detail}</span>
    </div>
    <Link className="gwRowAction" to={`/${issue.link.sectionId}${issue.link.search || ""}`}>
      {issue.link.label}
    </Link>
  </li>
);

export function PlcOverviewPage() {
  const notify = useNotify();
  const [evidence, setEvidence] = useState({});
  const [loading, setLoading] = useState(true);
  const generationRef = useRef(0);
  const outputCursorRef = useRef(0);

  const load = useCallback(async () => {
    const generation = ++generationRef.current;
    setLoading(true);
    const [hosts, controllers, deployments, modbus, s7comm, opcua] = await Promise.allSettled([
      listEdgeGateways(),
      listPLCControllers(),
      listPLCFleetRollouts(),
      getModbusServerStatus(),
      getS7CommServerStatus(),
      getOpcuaServerRoleStatus(),
    ]);
    if (generation !== generationRef.current) return;

    const controllerEvidence = evidenceFromResult(controllers, "Application runtime inventory could not be read.");
    const controllerList = controllerEvidence.state === "available"
      ? (Array.isArray(controllerEvidence.data?.controllers) ? controllerEvidence.data.controllers : [])
      : [];
    const runningControllerList = controllerList.filter((controller) => (
      controller?.actual?.authenticated
      && !controller.actual.stale
      && controller.actual.state === "running"
    ));
    let outputs = unavailableEvidence("Application output-delivery evidence could not be read.");
    let nextOutputCursor = outputCursorRef.current;
    if (controllerEvidence.state === "available") {
      try {
        const outputPage = await readBoundedControllerOutputs(runningControllerList, outputCursorRef.current);
        nextOutputCursor = outputPage.nextCursor;
        outputs = availableEvidence(outputPage);
      } catch (_error) {
        outputs = unavailableEvidence("Application output-delivery evidence could not be read.");
      }
    }
    if (generation !== generationRef.current) return;
    outputCursorRef.current = nextOutputCursor;

    const next = {
      runtimeHosts: evidenceFromResult(hosts, "Runtime-host inventory could not be read."),
      controllers: controllerEvidence,
      deployments: evidenceFromResult(deployments, "Deployment inventory could not be read."),
      outputs,
      protocols: {
        modbus: evidenceFromResult(modbus, "Modbus TCP service evidence could not be read."),
        s7comm: evidenceFromResult(s7comm, "S7Comm service evidence could not be read."),
        opcua: evidenceFromResult(opcua, "OPC UA service evidence could not be read."),
      },
    };
    setEvidence(next);
    setLoading(false);

    const failedSources = [hosts, controllers, deployments, modbus, s7comm, opcua]
      .filter((result) => result.status === "rejected").length;
    if (failedSources > 0) {
      notify({
        type: "warning",
        title: "Some PLC status is unavailable",
        message: `${failedSources} part${failedSources === 1 ? "" : "s"} of the overview could not be loaded. Refresh to try again.`,
      });
    }
  }, [notify]);

  useEffect(() => {
    load();
    return () => { generationRef.current += 1; };
  }, [load]);

  const overview = useMemo(() => buildPLCOverview(evidence), [evidence]);
  const hostSummary = overview.summary.runtimeHosts;
  const controllerSummary = overview.summary.controllers;
  const connectionSummary = overview.summary.connections;
  const deploymentSummary = overview.summary.deployments;
  const setupEmpty = !loading && hostSummary?.total === 0 && controllerSummary?.total === 0 && deploymentSummary?.total === 0;
  const recentDeployment = Array.isArray(evidence.deployments?.data) ? evidence.deployments.data[0] : null;
  const askAIForSetup = () => {
    window.dispatchEvent(new CustomEvent("gw-open-ai-with-prompt", { detail: {
      prompt: "Help me set up my first PLC runtime, connect its I/O, open a PLC application, and deploy it safely.",
    } }));
  };

  return (
    <div className="gwContentGrid plcOverview" data-gw-card="plc_overview">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">PLC Overview</div>
            <div className="gwPageSubtitle">
              See what is running, what is connected, and what needs attention.
            </div>
          </div>
          <div className="gwHeaderActions">
            <button type="button" className="gwIconButton" onClick={load} disabled={loading} aria-label="Refresh overview" title="Refresh">
              <img src={refreshIcon} alt="" className="gwIcon" />
            </button>
          </div>
        </div>

        {setupEmpty ? (
          <div className="gwCardSection plcOverviewSetup" data-gw-card="plc_setup_journey">
            <div className="gwSectionTitle">Set up your first PLC application</div>
            <div className="gwCardSectionBody">
              <ol className="plcOverviewSetupSteps">
                <li className="isCurrent"><span>1</span><div><strong>Add a runtime</strong><small>Connect the Pi or industrial PC that will run the application.</small></div></li>
                <li><span>2</span><div><strong>Connect I/O</strong><small>Add GPIO or a supported protocol connection on that runtime.</small></div></li>
                <li><span>3</span><div><strong>Open a PLC application</strong><small>Create or open its PLC project in the editor.</small></div></li>
                <li><span>4</span><div><strong>Deploy and run</strong><small>Review the application and deploy it to the selected runtime.</small></div></li>
              </ol>
              <div className="plcOverviewSetupActions">
                <Link className="gwPrimaryBtn" to="/plc-runtime-fleet">Add runtime</Link>
                <button type="button" className="gwGhostBtn" onClick={askAIForSetup}>Ask AI to guide me</button>
              </div>
            </div>
          </div>
        ) : <>
        <div className="plcOverviewMetrics" aria-label="PLC summary">
          <GatewayMetric className="plcOverviewMetric"
            label="Runtimes"
            value={hostSummary ? `${hostSummary.online} / ${hostSummary.total}` : "Unavailable"}
            detail={hostSummary ? "Online now" : "Runtime status unavailable"}
          />
          <GatewayMetric className="plcOverviewMetric"
            label="Applications"
            value={controllerSummary ? `${controllerSummary.running} / ${controllerSummary.total}` : "Unavailable"}
            detail={controllerSummary ? "Running now" : "Application status unavailable"}
          />
          <GatewayMetric className="plcOverviewMetric"
            label="I/O connections"
            value={connectionSummary ? (connectionSummary.total > 0 ? `${connectionSummary.running} / ${connectionSummary.total}` : "0") : "Unavailable"}
            detail={connectionSummary
              ? connectionSummary.total > 0 ? connectionSummary.complete ? "Connected now" : "Reported connected now" : "None configured"
              : "Connection status unavailable"}
          />
        </div>

        <div className={recentDeployment ? "plcOverviewColumns plcOverviewStatusColumns" : "plcOverviewStatusColumns"}>
          <div className="gwCardSection plcOverviewAttention" data-gw-card="plc_overview_attention">
            <div className="gwSectionTitleRow gwSectionTitle">
              <span>Needs attention</span>
              <span className="gwSectionBadge">{loading ? "Reading" : overview.issues.length}</span>
            </div>
            <div className="gwCardSectionBody">
              {loading ? <div className="gwHint">Refreshing current status without changing plant state…</div> : null}
              {!loading && overview.issues.length === 0 ? (
                <div className="gwNotice isSoft">
                  No current problem was reported.
                </div>
              ) : null}
              {!loading && overview.issues.length > 0 ? (
                <ul className="plcOverviewIssueList">
                  {overview.issues.map((issue) => <IssueRow issue={issue} key={issue.key} />)}
                </ul>
              ) : null}
            </div>
          </div>

          {recentDeployment ? <div className="gwCardSection" data-gw-card="plc_overview_recent_deployment">
            <div className="gwSectionTitle">Recent application activity</div>
            <div className="gwCardSectionBody plcOverviewRecentDeployment">
              <strong>{recentDeployment.name || "PLC deployment"}</strong>
              <span>{String(recentDeployment.phase || "Status unavailable").replaceAll("_", " ")}</span>
              <Link className="gwRowAction" to="/plc-deployments">Open deployment history</Link>
            </div>
          </div> : null}
        </div>

        <details className="gwCardSection plcOverviewTechnical" data-gw-card="plc_overview_diagnostics">
          <summary className="gwSectionTitle plcOverviewTechnicalSummary">
            <span>
              <strong>Technical details</strong>
              <small>Advanced application and protocol status</small>
            </span>
            <img src={chevronIcon} alt="" className="gwIcon plcOverviewTechnicalChevron" />
          </summary>
          <div className="gwCardSectionBody gwFormStack">
            <div className="plcOverviewCoverage">
              {overview.technicalDetails.map((item) => (
                <div key={item.key}>
                  <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                  <EvidenceState state={item.state} />
                </div>
              ))}
            </div>
            <div className="plcOverviewAdvancedLinks">
              <Link className="gwGhostBtn" to="/plc-compatibility">View supported PLC features</Link>
            </div>
          </div>
        </details>
        </>}
      </section>
    </div>
  );
}
